import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/SupabaseAuthContext';

export const SupabaseAuthCallback = () => {
  const { completeOAuth, verifyOtp, establishSession } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);
  const [message, setMessage] = useState('Completing secure sign in...');

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    // Supabase can deliver the credential in several shapes depending on the
    // flow (OAuth PKCE code, emailed one-time token_hash, or tokens in the URL
    // hash from the GoTrue verify redirect). Handle all of them.
    const query = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));

    const providerError = query.get('error_description') || hash.get('error_description');
    const code = query.get('code');
    const tokenHash = query.get('token_hash');
    const type = query.get('type') || hash.get('type');
    const accessToken = hash.get('access_token');
    const refreshToken = hash.get('refresh_token');

    // Invite and recovery links must land on the set-password screen; everything
    // else (OAuth, magic link, signup confirmation) goes straight to the app.
    const passwordFlow = type === 'invite' || type === 'recovery';

    const goToLogin = (msg) =>
      navigate('/login', { replace: true, state: { authError: msg } });

    const run = async () => {
      if (tokenHash && type) {
        await verifyOtp({ tokenHash, type });
      } else if (accessToken && refreshToken) {
        await establishSession({ accessToken, refreshToken });
      } else if (code) {
        await completeOAuth(code);
      } else {
        goToLogin('The sign-in link is invalid or expired.');
        return;
      }
      navigate(passwordFlow ? '/set-password' : '/dashboard', { replace: true });
    };

    if (providerError) {
      goToLogin(providerError);
      return;
    }

    run().catch((error) => {
      const msg = error.message || 'Unable to complete sign in.';
      setMessage(msg);
      window.setTimeout(() => goToLogin(msg), 1200);
    });
  }, [completeOAuth, verifyOtp, establishSession, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#196a70] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-600 font-body">{message}</p>
      </div>
    </div>
  );
};
